import React from "react";
import { Box, Typography } from "@mui/material";
import { Accordion, Button, Divider } from "@dishopsaas/design-system";

import { displayPriceFormat, getColors } from "../../../utils";
import {
  checkOrder,
  getBillingFlags,
  submitOrder,
} from "../PaymentSummary.services";
import { getTitleFromOffer } from "../../../components/ThresholdMarkertingProgress/ThresholdMarketingProgress.service";
import {
  LOYALTY,
  ORDER_TYPE_DELIVERY,
  PRIMARY,
  PROMOTION_PERCENTAGE,
  PROMOTION_FREE_ORDER,
  FREE_ORDER_LABEL,
  renderOrderStatusRow,
} from "../../../constants";
import {
  BillingRowProps,
  BillingProps,
} from "../../../1_types/paymentSummaryTypes";
import LoyaltyBanner from "../../../components/LoyaltyBanner/LoyaltyBanner";
import PromoCodeSection from "./PromoCodeSection";
import MinCartPriceInfo from "../../../components/Order/components/MinCartPriceInfo";
import { useAppDispatch } from "../../../redux/hook";
import { showMessageModal } from "../../../redux/actions";
import { getAddress } from "../../../components/InfoBar/InfoBar.services";
import { getAllowPromoCodes } from "../../../utils/promotionUtils";
import Loyalty from "../../../components/Loyalty/Loyalty.view";
import { useAppSelector } from "../../../redux/hook";
const rowSx = {
  mb: 3,
  display: "flex",
  justifyContent: "space-between",
};

export const CardBox: React.FC<{
  children: React.ReactNode;
  sx?: any;
}> = ({ children, sx = {} }) => (
  <Box
    sx={{
      color: getColors().text,
      borderRadius: 1,
      bgcolor: getColors().bgwhite,
      border: getColors().border,
      fontFamily: "PP Right Grotesk Casual Variable",
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const BillingRow: React.FC<BillingRowProps> = ({
  label,
  value,
  valueLabel,
  isNegative = false,
  shouldDisablePromo = false,
  isFee = false,
  showInfo = false,
  infoComponent,
}) => {
  if (!valueLabel && (value === undefined || value === null)) {
    return null;
  }

  const formattedValue = valueLabel ?? displayPriceFormat(value, !isFee);

  const labelSx = {
    textTransform: "none",
    color: shouldDisablePromo ? "red" : "inherit",
    textDecoration: shouldDisablePromo ? "line-through" : "none",
  };

  return (
    <Box sx={rowSx}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body1" sx={labelSx}>
          {label}
        </Typography>

        {showInfo && infoComponent}
      </Box>

      <Typography
        variant="body1"
        sx={{
          ...labelSx,
          fontWeight: 600,
          color: shouldDisablePromo
            ? "error.main"
            : isNegative
              ? "success.main"
              : "inherit",
        }}
      >
        {isNegative ? "-" : ""}
        {isNegative ? value : formattedValue}
      </Typography>
    </Box>
  );
};

const Billing: React.FC<BillingProps> = ({
  totalProductPriceOrder,
  totalPriceOrder,
  promotion,
  managementFee,
  expanded,
  setExpanded,
  paymentType,
  card,
  commande,
  selectTime,
  setLoading,
  isPhoneIncorrect,
  setChoosePayment,
  phoneObj,
  phone,
  setDateState,
  stripe,
  loading,
  thresholdOffer,
  marketingCampaignPromoAmount,
  freeDelivery,
  orderType,
  address,
  promoPath,
  nbUsed,
  orderTime,
  shopName,
  shopConfiguration,
  marketing,
  basketDisplayContract,
}) => {
  const { isDeliveryFree, isClickAndCollectPromo, shouldShowDeliveryFee } =
    getBillingFlags({
      promotion,
      freeDelivery,
      orderType,
    });

  const isMinBasketPrice =
    !!promotion?.minBasketPrice &&
    totalProductPriceOrder < promotion.minBasketPrice;

  const isLoyalty = promotion?.promoCode === LOYALTY;

  const isPromoApplied = !!promotion?.promoCode && !isLoyalty;

  const textPromo = isLoyalty
    ? `Fidélité ${promotion?.name || ""}`
    : `Promotion ${promotion?.promoCode}`;

  const userServicePrice = address?.deliveryPrice ?? 0;

  const dispatch = useAppDispatch();

  const allowPromoCodes = getAllowPromoCodes(
    shopConfiguration,
    marketing,
    commande,
  );

  const isPromoNotAllowed = Boolean(promotion) && !allowPromoCodes;

  const shouldDisablePromo = isPromoNotAllowed || isMinBasketPrice;

  const shouldStrikePromotion = isPromoNotAllowed || isMinBasketPrice;

  const totalProductPriceWithoutFees =
    totalPriceOrder -
    ((freeDelivery ? 0 : userServicePrice) + (managementFee ?? 0));

  const displayedSubtotal = basketDisplayContract
    ? basketDisplayContract.gross
    : totalProductPriceOrder;

  const displayedPromotionAmount = basketDisplayContract
    ? basketDisplayContract.discount || null
    : marketingCampaignPromoAmount;

  const displayedSubtotalAfterDiscount = basketDisplayContract
    ? basketDisplayContract.net
    : totalProductPriceWithoutFees;

  const displayedMarketingCampaignLabel =
    basketDisplayContract || commande
      ? "Campagne Marketing"
      : getTitleFromOffer(thresholdOffer);

  const showDefaultCardModal = () => {
    const confirmOrderText = (
      <>
        Je confirme passer commande{" "}
        <strong>{renderOrderStatusRow(orderType)?.toLowerCase()}</strong>
        {orderTime?.value && (
          <>
            {" "}
            à <strong>{orderTime.value}</strong>
          </>
        )}{" "}
        {orderType === ORDER_TYPE_DELIVERY ? (
          <>
            à l’adresse <strong>{getAddress()}</strong>.
          </>
        ) : (
          <>
            au restaurant <strong>{shopName}</strong>.
          </>
        )}
      </>
    );
    dispatch(
      showMessageModal(confirmOrderText, {
        showCancel: true,
        text: "confirmation",
        buttonName: "Confirmer",
        maxWidth: 480,
        secondaryButtonSx: {
          maxWidth: 150,
        },
        onClick: async () => {
          dispatch(showMessageModal(null));
          await submitOrder({
            paymentType,
            card,
            stripe,
            setLoading,
            phoneObj,
            phone,
            setChoosePayment,
            setDateState,
          });
        },
      }),
    );
  };
  const renderPromotion = () => {
    if (!promotion) {
      return null;
    }

    let displayValue = "";
    if (
      typeof promotion.type === "string" &&
      promotion.type.includes(PROMOTION_PERCENTAGE)
    ) {
      displayValue = promotion.type;
    } else if (typeof promotion.type === "number") {
      displayValue = displayPriceFormat(promotion.type);
    }

    return (
      <BillingRow
        label={textPromo}
        value={displayValue}
        valueLabel={promotion.type === PROMOTION_FREE_ORDER ? FREE_ORDER_LABEL : undefined}
        isNegative={promotion.type !== PROMOTION_FREE_ORDER}
        shouldDisablePromo={shouldDisablePromo}
        showInfo={shouldStrikePromotion}
        infoComponent={
          <MinCartPriceInfo
            promoType={isLoyalty ? "loyalty" : "code-promo"}
            minBasketPrice={promotion?.minBasketPrice}
            allowPromoCodes={allowPromoCodes}
          />
        }
      />
    );
  };

  const renderLoyaltyBanner = () => {
    if (!isLoyalty) {
      return null;
    }

    return (
      <Box sx={{ pb: 4 }}>
        <LoyaltyBanner commande={commande} />
      </Box>
    );
  };
  const pendingOrder = useAppSelector(
    (state) => state.pendingOrderReducer
  );
  const { userConnected, isUserAnonymous } = useAppSelector(
    (state) => state.userReducer
  );
  const {
    totalProductPriceOrder: totalProductPrice,
  } = pendingOrder;
  const showReduction = (userConnected || isUserAnonymous);
  const { border } = getColors();

  const showDiscountPrice = totalProductPriceOrder > totalPriceOrder;

  return (
    <CardBox>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ pb: 4 }}>
          Récapitulatif de la commande
        </Typography>
        <BillingRow label="Sous-Total" value={displayedSubtotal} />

        {promotion &&
          !isClickAndCollectPromo &&
          (!isDeliveryFree || promotion.type === PROMOTION_FREE_ORDER) &&
          renderPromotion()}
        {!!displayedPromotionAmount && (
          <BillingRow
            label={displayedMarketingCampaignLabel}
            isNegative
            value={displayedPromotionAmount}
          />
        )}
        {!!(promotion || displayedPromotionAmount) && (
          <BillingRow
            label="Sous total après remise"
            value={displayedSubtotalAfterDiscount}
          />
        )}
        {(!!shouldShowDeliveryFee || !!managementFee) && (
          <>
            {!!shouldShowDeliveryFee && (
              <BillingRow
                label="Frais de livraison"
                value={userServicePrice}
                isFee={true}
              />
            )}
            {!!managementFee && (
              <BillingRow
                label="Frais et services"
                value={managementFee}
                isFee={true}
              />
            )}
          </>
        )}
        {isDeliveryFree && promotion?.type !== PROMOTION_FREE_ORDER && userServicePrice && (
          <BillingRow label="Livraison offerte" value={-userServicePrice} />
        )}

        {isClickAndCollectPromo && renderPromotion()}
      </Box>
      <Box sx={{ px: 1, pb: 1 }}>
        {expanded && (
          <Divider
            sx={{
              borderBottomColor: border,
            }}
          />
        )}

        <Accordion
          title="Code Promo"
          leftIcon="Gift"
          expanded={expanded}
          onChange={setExpanded}
        >
          <PromoCodeSection
            promotion={promotion}
            isPromoApplied={isPromoApplied}
            promoPath={promoPath}
            nbUsed={nbUsed}
            totalProductPriceOrder={totalProductPriceOrder}
          />
        </Accordion>
      </Box>

      <Divider
        sx={{
          borderBottomColor: border,
        }}
      />
      <Box
        sx={{
          pt: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 3,
          pb: 3,
        }}
      >
        {renderLoyaltyBanner()}
        {showReduction && !isUserAnonymous && (
          <div>
            <Loyalty totalProductPrice={totalProductPrice} />
          </div>
        )}
        <Box display="flex" justifyContent="space-between" pb={2}>
          <Typography variant="h5">TOTAL</Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {showDiscountPrice && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  textDecoration: "line-through",
                }}
              >
                {displayPriceFormat(totalProductPriceOrder)}
              </Typography>
            )}

            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: showDiscountPrice ? "success.main" : null,
              }}
            >
              {displayPriceFormat(totalPriceOrder)}
            </Typography>
          </Box>
        </Box>
        <Button
          loading={loading}
          colorType={PRIMARY}
          sx={{ width: "100%" }}
          label="COMMANDER MAINTENANT"
          onClick={async () => {
            await checkOrder({
              paymentType,
              card,
              commande,
              selectTime,
              isPhoneIncorrect,
              phone,
              setDateState,
              showDefaultCardModal,
            });
          }}
        />
      </Box>
    </CardBox>
  );
};

export default Billing;
